// ViewSalesInvoiceDetails.js - Updated version
import React, { useState, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faTimesCircle,
  faSpinner,
  faClose,
  faHistory,
  faRemove,
  faUndo,
  faExchangeAlt,
  faCircleCheck,
  faCancel,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import MessageModal from "./MessageModal";
import mainLogo from "./images/mainlogo.png";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import SecureLS from "secure-ls";
import { pdf } from "@react-pdf/renderer";
import PaymentModal from "./PaymentModal";
import PaymentHistory from "./PaymentHistory";
import InvoiceReceiptPdf from "./InvoiceReceiptPdf";

const ls = new SecureLS({ encodingType: "aes" });
const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function EditSalesOrder() {
  const { stateNavigator, data } = useNavigationEvent();
  const saleId = data?.salesId;

  const [invoiceData, setInvoiceData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [cashierData, setCashierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentAddModal, setShowPaymentAddModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [processingReturn, setProcessingReturn] = useState(false);
  const [selectedItemForReturn, setSelectedItemForReturn] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [returnReason, setReturnReason] = useState("");
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState("");
  const [successModalColor, setSuccessModalColor] = useState("green");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch invoice data with included relationships
  const {
    data: invoiceResponse,
    error: fetchError,
    mutate: mutateInvoice,
  } = useSWR(
    saleId
      ? `${process.env.REACT_APP_SALES_ORDER_API}/${saleId}?include=field_cashier,field_customer`
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const { data: invoiceorderedItems, mutate: mutateOrderItems } = useSWR(
    `${process.env.REACT_APP_POS_SINGLE_ORDER_API}/${saleId}`,
    fetcher,
  );

  const orderedproducts = invoiceorderedItems?.order;

  useEffect(() => {
    if (invoiceResponse?.data) {
      const order = invoiceResponse.data;
      setInvoiceData(order);

      if (invoiceResponse.included) {
        const customer = invoiceResponse.included.find(
          (item) => item.type === "node--customer",
        );
        if (customer) {
          setCustomerData({
            name: customer.attributes.title,
            email: customer.attributes.field_customer_email,
            phone: customer.attributes.field_phone_number,
            address: customer.attributes.field_customer_address || "N/A",
          });
        }

        const cashier = invoiceResponse.included.find(
          (item) => item.type === "user--user",
        );
        if (cashier) {
          setCashierData({
            name: cashier.attributes.name,
            email: cashier.attributes.mail,
          });
        }
      }

      setLoading(false);
    }

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
    }
  }, [invoiceResponse, fetchError]);

  // Handle product return with success modal
  const handleReturnItem = async () => {
    if (!selectedItemForReturn || returnQuantity <= 0) {
      alert("Please select a valid quantity to return");
      return;
    }

    if (returnQuantity > selectedItemForReturn.quantity) {
      alert(`Cannot return more than ${selectedItemForReturn.quantity} items`);
      return;
    }

    if (!returnReason.trim()) {
      alert("Please provide a reason for the return");
      return;
    }

    setProcessingReturn(true);

    try {
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        alert("Authentication failed");
        return;
      }

      // Calculate return amounts
      const unitPrice = parseFloat(selectedItemForReturn.unit_price || 0);
      const returnAmount = returnQuantity * unitPrice;
      const currentTotalPaid = parseFloat(
        invoiceorderedItems?.order?.total_paid || 0,
      );

      // Determine refund amount
      let refundAmount = 0;
      let updateType = "return_only";

      if (currentTotalPaid >= returnAmount) {
        refundAmount = returnAmount;
        updateType = "return_with_refund";
      }

      // Create return request
      const returnData = {
        order_id: saleId,
        order_uuid: saleId,
        items: [
          {
            item_uuid: selectedItemForReturn.uuid || selectedItemForReturn.id,
            product_uuid: selectedItemForReturn.product_uuid,
            product_title: selectedItemForReturn.product_title,
            quantity: returnQuantity,
            unit_price: unitPrice,
            return_amount: returnAmount,
            reason: returnReason,
          },
        ],
        total_return_amount: returnAmount,
        refund_amount: refundAmount,
        return_type: updateType,
        notes: `Return processed for ${returnQuantity} x ${selectedItemForReturn.product_title}. Reason: ${returnReason}`,
      };

      const returnUrl = `${process.env.REACT_APP_BASE_URL}/pos/orders/${saleId}/return`;

      // Call your backend API for returns
      const response = await fetch(returnUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(returnData),
      });

      // Get the response text first to debug
      const responseText = await response.text();

      // Check if response is JSON
      let result = null;
      let isJson = false;

      try {
        result = JSON.parse(responseText);
        isJson = true;
      } catch (e) {
        console.error("Response is not valid JSON, it's HTML or text");
        isJson = false;
      }

      if (!response.ok) {
        // Handle error responses
        let errorMsg = "";
        if (
          responseText.includes("The website") ||
          responseText.includes("404") ||
          responseText.includes("not found")
        ) {
          errorMsg = `Return endpoint not found. Please check if the return API endpoint is implemented.`;
        } else if (
          responseText.includes("403") ||
          responseText.includes("forbidden")
        ) {
          errorMsg =
            "Access denied. You don't have permission to process returns.";
        } else if (responseText.includes("500")) {
          errorMsg = "Server error. Please contact support.";
        } else {
          errorMsg = `Failed to process return. Server responded with status ${response.status}.`;
        }

        setErrorMessage(errorMsg);
        setModalError(true);
        setProcessingReturn(false);
        return;
      }

      // Refresh order data
      await mutateOrderItems();
      await mutateInvoice();

      // Prepare success message
      let message = "";
      if (updateType === "return_with_refund" && refundAmount > 0) {
        message = `✅ Return processed successfully!\n\nItem returned: ${returnQuantity} x ${selectedItemForReturn.product_title}\nRefund amount: GH₵${refundAmount.toFixed(2)}\n\nRefund will be processed to the original payment method.`;
        setSuccessModalColor("green");
      } else {
        message = `✅ Return processed successfully!\n\nItem returned: ${returnQuantity} x ${selectedItemForReturn.product_title}\nAmount credited: GH₵${returnAmount.toFixed(2)}\n\nThis amount has been deducted from your outstanding balance.`;
        setSuccessModalColor("green");
      }

      setSuccessModalMessage(message);
      setShowSuccessModal(true);

      // Close modal and reset states
      setShowReturnModal(false);
      setSelectedItemForReturn(null);
      setReturnQuantity(1);
      setReturnReason("");
    } catch (error) {
      console.error("Error processing return:", error);
      setErrorMessage(error.message || "Failed to process return");
      setModalError(true);
    } finally {
      setProcessingReturn(false);
    }
  };

  // Alternative: If you need to just remove item without refund (for unpaid orders)
  const handleRemoveItemFromOrder = async (item) => {
    if (
      !window.confirm(
        `Are you sure you want to remove "${item.product_title}" from this order?`,
      )
    ) {
      return;
    }

    setProcessingReturn(true);

    try {
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        alert("Authentication failed");
        return;
      }

      const itemTotal = parseFloat(item.line_total || 0);
      const currentTotalAmount = parseFloat(
        invoiceorderedItems?.order?.total_amount || 0,
      );
      const newTotalAmount = currentTotalAmount - itemTotal;

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/orders/${saleId}/items/${item.uuid || item.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            Authorization: authHeader,
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove item");
      }

      // Refresh order data
      await mutateOrderItems();
      await mutateInvoice();

      alert(
        `Item removed successfully! Order total updated to GH₵${newTotalAmount.toFixed(2)}`,
      );
    } catch (error) {
      console.error("Error removing item:", error);
      alert(`Failed to remove item: ${error.message}`);
    } finally {
      setProcessingReturn(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById("invoice-print-content");
    const originalContent = document.body.innerHTML;

    if (printContent) {
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    } else {
      window.print();
    }
  };

  const printThermalInvoice = async () => {
    const blob = await pdf(
      <InvoiceReceiptPdf
        invoiceData={invoiceData}
        customerData={customerData}
        items={invoiceorderedItems?.order?.items || []}
        total={total}
      />,
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Receipt-${
      invoiceData?.attributes?.field_order_number || "invoice"
    }.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBack = () => {
    stateNavigator.navigate("viewsalesregister");
  };

  const handlePaymentSuccess = (result) => {
    alert(`Payment of GH₵${result.amount_paid} added successfully!`);
    setTimeout(() => {
      mutateOrderItems();
      mutateInvoice();
    }, 500);
  };

  const handleAddPayment = () => {
    setShowPaymentAddModal(true);
  };

  const calculateTotals = () => {
    if (invoiceorderedItems?.order) {
      return {
        subtotal: Number(invoiceorderedItems.order.total_amount || 0),
        tax: 0,
        total: Number(invoiceorderedItems.order.total_amount || 0),
        totalPaid: Number(invoiceorderedItems.order.total_paid || 0),
        remainingBalance: Number(
          invoiceorderedItems.order.remaining_balance || 0,
        ),
        paymentStatus: invoiceorderedItems.order.payment_status || "unknown",
      };
    }

    if (!orderedproducts?.items?.length) {
      return {
        subtotal: 0,
        tax: 0,
        total: 0,
        totalPaid: 0,
        remainingBalance: 0,
        paymentStatus: "unknown",
      };
    }

    const subtotal = orderedproducts?.items?.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const price = Number(item.unit_price || 0);
      return sum + qty * price;
    }, 0);

    const total = orderedproducts?.items?.reduce((sum, item) => {
      return sum + Number(item.line_total || 0);
    }, 0);

    return {
      subtotal,
      tax: 0,
      total,
      totalPaid: total,
      remainingBalance: 0,
      paymentStatus: "paid",
    };
  };

  const { subtotal, tax, total, totalPaid, remainingBalance } =
    calculateTotals();

  const formatGhanaPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    let digits = phone.replace(/\D/g, "");
    if (digits.startsWith("0")) {
      digits = digits.substring(1);
    }
    if (!digits.startsWith("233")) {
      digits = `233${digits}`;
    }
    if (digits.length === 12) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(
        5,
        8,
      )} ${digits.slice(8)}`;
    }
    return phone;
  };

  const ghanaPhoneForWhatsApp = (phone) =>
    formatGhanaPhoneNumber(phone).replace(/\s/g, "");

  const sendInvoiceViaWhatsApp = () => {
    if (!orderedproducts?.items?.length) {
      alert("No invoice items available");
      return;
    }

    const phone = `${ghanaPhoneForWhatsApp(customerData?.phone || "")}`;

    let message = `*PANTALA LUXURY PERFUMES - RECEIPT*\n`;
    message += `Invoice #: ${invoiceData?.attributes?.field_order_number}\n`;
    message += `Date: ${new Date(invoiceData?.attributes?.created).toLocaleString("en-GH")}\n\n`;
    message += `Hi ${customerData?.name || "Customer"}!, Thank you for shopping with us\n\n`;
    message += `*Items:*\n`;

    invoiceorderedItems?.order?.items?.forEach((item) => {
      message += `• ${item.product_title}\n`;
      message += `  ${item.quantity} x GH₵${Number(item.unit_price).toFixed(2)} = GH₵${Number(item.line_total).toFixed(2)}\n\n`;
    });

    message += `*Payment Summary:*\n`;
    message += `Total Amount: GH₵${Number(total).toFixed(2)}\n`;
    message += `Total Paid: GH₵${totalPaid.toFixed(2)}\n`;
    message += `Balance Due: GH₵${remainingBalance.toFixed(2)}\n\n`;
    message += `Thank you for your business 🙏\n`;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleSMS = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/sms/send`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone: ghanaPhoneForWhatsApp(customerData?.phone || ""),
            message: `Hi ${customerData?.name || "Customer"}, Thank you for shopping at Pantala Luxury Perfumes. Receipt# ${invoiceData?.attributes?.field_order_number} - Total: ${Number(total).toFixed(2)}. Total Paid: ${totalPaid.toFixed(2)}. Remaining Balance: ${remainingBalance.toFixed(2)}. We appreciate your business!`,
          }),
        },
      );

      if (!response.ok) throw new Error("SMS failed");
      alert("SMS sent successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to send SMS");
    }
  };

  const handleEmail = () => {
    alert("Email Invoice will be available soon.");
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <TopActionMenus
              AddTitle="Invoice Details"
              backIcon={faArrowLeft}
              backLable="Back to Register"
            />
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                size="3x"
                style={styles.loadingSpinner}
              />
              <p style={styles.loadingText}>Loading invoice details...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <TopActionMenus
              AddTitle="Invoice Details"
              buttonLabel="Back to Register"
              icon={faArrowLeft}
              onClick={handleBack}
            />
            <div style={styles.errorContainer}>
              <FontAwesomeIcon icon={faTimesCircle} style={styles.errorIcon} />
              <h3>Invoice Not Found</h3>
              <p>{error || "The requested invoice could not be found."}</p>
              <button style={styles.backButton} onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Sales Register
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus
            AddTitle="Invoice Details"
            buttonLabel="Back to Register"
            icon={faArrowLeft}
            onClick={handleBack}
          />

          {/* PRINTABLE CONTENT */}
          <div id="invoice-print-content" style={styles.printContent}>
            {/* INVOICE HEADER */}
            <div style={styles.invoiceHeader}>
              <div style={styles.companyInfo}>
                <div style={styles.companyLogo}>
                  <img
                    src={mainLogo}
                    alt="Prosep Services Logo"
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
                <div>
                  <p style={styles.companyContact}>
                    Phone: 0244770192 / 0266090979
                    <br />
                    Email: pantalaluxuryperfumes@gmail.com
                  </p>
                </div>
              </div>

              <div style={styles.invoiceTitleContainer}>
                <h2 style={styles.invoiceTitle}>INVOICE</h2>
                <div style={styles.invoiceMeta}>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Invoice #:</span>
                    <span style={styles.metaValue}>
                      {invoiceData.attributes.field_order_number}
                    </span>
                  </div>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Order ID:</span>
                    <span style={styles.metaValue}>
                      {invoiceData.attributes.drupal_internal__nid}
                    </span>
                  </div>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Date:</span>
                    <span style={styles.metaValue}>
                      {new Date(
                        invoiceData.attributes.created,
                      ).toLocaleDateString("en-GH", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* CUSTOMER AND PAYMENT INFO */}
            <div style={styles.infoGrid}>
              <div style={styles.infoCard}>
                <div style={styles.infoHeader}>
                  <h3 style={styles.infoTitle}>Bill To</h3>
                </div>
                {customerData ? (
                  <div style={styles.infoContent}>
                    <h4 style={styles.customerName}>{customerData.name}</h4>
                    {customerData.email && <p>Email: {customerData.email}</p>}
                    {customerData.phone && <p>Phone: {customerData.phone}</p>}
                    {customerData.address && (
                      <p>Address: {customerData.address}</p>
                    )}
                  </div>
                ) : (
                  <p>Customer information not available</p>
                )}
              </div>

              <div style={styles.infoCard}>
                <div style={styles.infoContent}>
                  <h4 style={styles.paymentHistoryTitle}>Payment Details</h4>
                  {cashierData && (
                    <div style={styles.cashierInfo}>
                      <p>
                        <strong>Cashier:</strong> {cashierData.name}
                      </p>
                    </div>
                  )}
                  <button
                    style={styles.paymenthistorybtn}
                    onClick={() => setShowPaymentHistoryModal(true)}
                  >
                    <FontAwesomeIcon icon={faHistory} /> Payment History
                  </button>
                </div>
              </div>
            </div>

            {/* ITEMS TABLE */}
            <div style={styles.itemsCard}>
              <div style={styles.tableContainer}>
                <table style={styles.itemsTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Item Name</th>
                      <th style={styles.th}>SKU</th>
                      <th style={styles.th}>Barcode</th>
                      <th style={styles.th}>Quantity</th>
                      <th style={styles.th}>Unit Price</th>
                      <th style={styles.th}>Total</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedproducts?.items?.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={styles.noItems}>
                          No items found in this order
                        </td>
                      </tr>
                    ) : (
                      orderedproducts?.items?.map((item, index) => (
                        <tr
                          key={item.id || index}
                          style={
                            index % 2 === 0 ? styles.evenRow : styles.oddRow
                          }
                        >
                          <td style={styles.td}>{index + 1}</td>
                          <td style={styles.td}>
                            {item.product_title || "N/A"}
                          </td>
                          <td style={styles.td}>{item.sku || "N/A"}</td>
                          <td style={styles.td}>{item.barcode || "N/A"}</td>
                          <td style={styles.td}>{item.quantity || "N/A"}</td>
                          <td style={styles.td}>
                            GH₵{parseFloat(item.unit_price || 0).toFixed(2)}
                          </td>
                          <td style={styles.td}>GH₵{item.line_total}</td>
                          <td style={styles.td}>
                            <div style={styles.actionButtonsGroup}>
                              <button
                                style={styles.returnButton}
                                onClick={() => {
                                  setSelectedItemForReturn(item);
                                  setReturnQuantity(1);
                                  setReturnReason("");
                                  setShowReturnModal(true);
                                }}
                                title="Return Item"
                                disabled={processingReturn}
                              >
                                <FontAwesomeIcon icon={faUndo} /> Return
                              </button>
                              {remainingBalance === 0 && totalPaid === 0 && (
                                <button
                                  style={styles.removeButton}
                                  onClick={() =>
                                    handleRemoveItemFromOrder(item)
                                  }
                                  title="Remove Item"
                                  disabled={processingReturn}
                                >
                                  <FontAwesomeIcon icon={faRemove} /> Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* TOTALS */}
            <div style={styles.totalsCard}>
              <div style={styles.totalsGrid}>
                <div>
                  <div style={styles.totalsLeft}>
                    <h4 style={styles.notesTitle}>Notes</h4>
                    <p style={styles.notesText}>
                      {invoiceData.attributes.field_sales_order_notes || "N/A"}
                    </p>
                  </div>
                  <div style={styles.termsCard}>
                    <h4 style={styles.termsTitle}>Terms and Conditions</h4>
                    <p style={styles.termsText}>
                      Information will be available soon.
                    </p>
                  </div>
                </div>

                <div style={styles.totalsRight}>
                  <div style={styles.orderSummarySection}>
                    <h4 style={styles.summaryTitle}>Order Summary</h4>
                    <div style={styles.totalRow}>
                      <span>Subtotal:</span>
                      <span>GH₵{subtotal.toFixed(2)}</span>
                    </div>
                    <div style={styles.totalRow}>
                      <span>Tax:</span>
                      <span>GH₵{tax.toFixed(2)}</span>
                    </div>
                    <div style={styles.grandTotalRow}>
                      <span>
                        <strong>Total Amount:</strong>
                      </span>
                      <span>
                        <strong>GH₵{total.toFixed(2)}</strong>
                      </span>
                    </div>
                    <div style={styles.paymentSummarySection}>
                      <div style={styles.totalRow}>
                        <span>Total Paid:</span>
                        <span style={styles.paidAmount}>
                          GH₵{totalPaid.toFixed(2)}
                        </span>
                      </div>
                      <div style={styles.totalRow}>
                        <span>Balance Due:</span>
                        <span
                          style={
                            remainingBalance > 0
                              ? styles.balanceAmount
                              : styles.paidAmount
                          }
                        >
                          GH₵{remainingBalance.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {/* Return Modal */}
      {showReturnModal && selectedItemForReturn && (
        <div className="modal" style={styles.modalOverlay}>
          <div
            className="overlay"
            onClick={() => setShowReturnModal(false)}
          ></div>
          <div className="modal-content" style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header" style={styles.modalHeader}>
                <h3>Process Return</h3>
                <button
                  className="close-modal"
                  onClick={() => setShowReturnModal(false)}
                  style={styles.closeButton}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <div style={styles.returnForm}>
                <div style={styles.returnItemInfo}>
                  <p>
                    <strong>Product:</strong>{" "}
                    {selectedItemForReturn.product_title}
                  </p>
                  <p>
                    <strong>Unit Price:</strong> GH₵
                    {parseFloat(selectedItemForReturn.unit_price || 0).toFixed(
                      2,
                    )}
                  </p>
                  <p>
                    <strong>Quantity Purchased:</strong>{" "}
                    {selectedItemForReturn.quantity}
                  </p>
                </div>

                <div style={styles.formGroup}>
                  <label>Return Quantity:</label>
                  <input
                    type="number"
                    min="1"
                    max={selectedItemForReturn.quantity}
                    value={returnQuantity}
                    onChange={(e) =>
                      setReturnQuantity(parseInt(e.target.value) || 1)
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label>Return Amount:</label>
                  <input
                    type="text"
                    readOnly
                    value={`GH₵${(returnQuantity * parseFloat(selectedItemForReturn.unit_price || 0)).toFixed(2)}`}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label>Reason for Return:</label>
                  <select
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Select a reason</option>
                    <option value="Damaged Product">Damaged Product</option>
                    <option value="Wrong Item">Wrong Item</option>
                    <option value="Customer Changed Mind">
                      Customer Changed Mind
                    </option>
                    <option value="Defective Item">Defective Item</option>
                    <option value="Expired Product">Expired Product</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {returnReason === "Other" && (
                  <div style={styles.formGroup}>
                    <label>Specify Reason:</label>
                    <textarea
                      value={returnReason === "Other" ? returnReason : ""}
                      onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Please specify the reason for return"
                      rows="3"
                      style={styles.textarea}
                    />
                  </div>
                )}

                <div style={styles.returnInfo}>
                  <div style={styles.infoBox}>
                    <FontAwesomeIcon
                      icon={faExchangeAlt}
                      style={styles.infoIcon}
                    />
                    <p>
                      {totalPaid >=
                      returnQuantity *
                        parseFloat(selectedItemForReturn.unit_price || 0)
                        ? "This return will be refunded to the customer's payment method."
                        : "This return will be credited to the customer's account balance."}
                    </p>
                  </div>
                </div>

                <div style={styles.modalActions}>
                  <button
                    style={styles.cancelButton}
                    onClick={() => setShowReturnModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.submitButton}
                    onClick={handleReturnItem}
                    disabled={
                      processingReturn || !returnReason || returnQuantity <= 0
                    }
                  >
                    {processingReturn ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faUndo} />
                    )}
                    {processingReturn ? " Processing..." : " Process Return"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Add Modal */}
      {showPaymentAddModal && (
        <div className="modal" style={styles.modalOverlay}>
          <div
            className="overlay"
            onClick={() => setShowPaymentAddModal(false)}
          ></div>
          <div className="modal-content" style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header" style={styles.modalHeader}>
                <h3>Add Payment</h3>
                <button
                  className="close-modal"
                  onClick={() => setShowPaymentAddModal(false)}
                  style={styles.closeButton}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <PaymentModal
                isOpen={showPaymentAddModal}
                onClose={() => setShowPaymentAddModal(false)}
                orderUuid={saleId}
                balance={remainingBalance}
                onSuccess={handlePaymentSuccess}
              />
              {/* Success Modal for Returns */}
              {showSuccessModal && (
                <MessageModal
                  leftIcon={faCircleCheck}
                  color={successModalColor}
                  message={successModalMessage}
                  onClose={() => {
                    setShowSuccessModal(false);
                    setSuccessModalMessage("");
                  }}
                  rightIcon={faClose}
                />
              )}

              {/* Error Modal */}
              {modalError && (
                <MessageModal
                  leftIcon={faCancel}
                  color="red"
                  message={errorMessage}
                  onClose={() => {
                    setModalError(false);
                    setErrorMessage("");
                  }}
                  rightIcon={faClose}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistoryModal && (
        <div className="modal" style={styles.modalOverlay}>
          <div
            className="overlay"
            onClick={() => setShowPaymentHistoryModal(false)}
          ></div>
          <div className="modal-content" style={styles.modalContent}>
            <div className="modal-body">
              <div className="modal-header" style={styles.modalHeader}>
                <h3>Payment History</h3>
                <button
                  className="close-modal"
                  onClick={() => setShowPaymentHistoryModal(false)}
                  style={styles.closeButton}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <PaymentHistory invoiceorderedItems={invoiceorderedItems} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "400px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  loadingSpinner: { color: "#007bff", marginBottom: "20px" },
  loadingText: { color: "#666", fontSize: "16px" },
  errorContainer: {
    textAlign: "center",
    padding: "60px 20px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  errorIcon: { fontSize: "60px", color: "#dc3545", marginBottom: "20px" },
  backButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "20px",
  },
  printContent: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    padding: "30px",
    marginBottom: "20px",
  },
  invoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f0f0f0",
  },
  companyInfo: { alignItems: "center", gap: "2px" },
  companyLogo: {
    background: "transparent",
    color: "white",
    width: "100px",
    height: "68px",
    marginBottom: "8px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  companyContact: { color: "#666", fontSize: "14px", lineHeight: "1.2" },
  invoiceTitleContainer: { textAlign: "left" },
  invoiceTitle: {
    margin: "0 0 4px 40px",
    fontSize: "32px",
    fontWeight: "700",
    color: "#007bff",
  },
  invoiceMeta: { display: "flex", flexDirection: "column", gap: "2px" },
  metaItem: { display: "flex", gap: "6px" },
  metaLabel: {
    fontWeight: "600",
    color: "#666",
    minWidth: "100px",
    textAlign: "right",
  },
  metaValue: { fontWeight: "600", color: "#333" },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  infoCard: {
    background: "#f8f9fa",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "15px",
  },
  infoTitle: { margin: "0", fontSize: "18px", color: "#333" },
  infoContent: { color: "#555" },
  customerName: { margin: "0 0 10px 0", fontSize: "20px", color: "#333" },
  cashierInfo: { marginTop: "10px", paddingTop: "10px" },
  itemsCard: { marginBottom: "20px" },
  tableContainer: {
    overflowX: "auto",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  itemsTable: { width: "100%", borderCollapse: "collapse" },
  th: {
    background: "#333",
    padding: "12px",
    textAlign: "left",
    borderBottom: "2px solid #444",
    fontWeight: "normal",
    color: "#fff",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #e0e0e0",
    color: "#555",
    verticalAlign: "top",
  },
  noItems: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
    fontStyle: "italic",
  },
  evenRow: { background: "#fafafa" },
  oddRow: { background: "white" },
  totalsCard: {
    background: "#f8f9fa",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    marginBottom: "20px",
  },
  totalsGrid: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: "40px",
  },
  totalsLeft: { paddingRight: "20px", flex: 1 },
  notesTitle: {
    margin: "0 0 15px 0",
    fontSize: "18px",
    color: "#333",
    fontWeight: "600",
  },
  notesText: {
    color: "#666",
    lineHeight: "1.6",
    fontSize: "14px",
    marginBottom: "20px",
  },
  totalsRight: { maxWidth: "400px" },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontWeight: "600",
    fontSize: "16px",
    color: "#666",
  },
  grandTotalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px 0",
    fontSize: "18px",
    color: "#333",
    borderTop: "2px solid #333",
  },
  actionButtons: { display: "flex", gap: "15px", marginTop: "20px" },
  printButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
  paymenthistorybtn: {
    background: "#C2C2C2",
    color: "black",
    border: "none",
    padding: "12px 18px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    marginTop: "16px",
  },
  termsCard: {
    background: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  termsTitle: {
    margin: "0 0 10px 0",
    fontSize: "16px",
    color: "#333",
    fontWeight: "600",
  },
  termsText: {
    margin: "0",
    color: "#666",
    fontSize: "14px",
    lineHeight: "1.6",
  },
  paidAmount: { fontWeight: "bold", color: "#28a745" },
  balanceAmount: { fontWeight: "bold", color: "#dc3545" },
  paymentHistoryTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "10px",
    color: "#333",
  },
  orderSummarySection: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  summaryTitle: {
    margin: "0 0 15px 0",
    fontSize: "16px",
    color: "#333",
    fontWeight: "600",
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: "8px",
  },
  paymentSummarySection: { paddingTop: "15px", borderTop: "2px solid #f0f0f0" },

  // Return styles
  actionButtonsGroup: { display: "flex", gap: "8px" },
  returnButton: {
    backgroundColor: "#ffc107",
    color: "#333",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  removeButton: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  // Modal styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: "12px",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid #e0e0e0",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#666",
  },
  returnForm: { padding: "20px" },
  returnItemInfo: {
    backgroundColor: "#f8f9fa",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  formGroup: { marginBottom: "15px" },
  input: {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
  },
  select: {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
  },
  textarea: {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    fontFamily: "inherit",
  },
  returnInfo: { marginBottom: "20px" },
  infoBox: {
    backgroundColor: "#e3f2fd",
    padding: "12px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  infoIcon: { color: "#1976d2", fontSize: "20px" },
  modalActions: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    marginTop: "20px",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  submitButton: {
    padding: "10px 20px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};
